import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useView, type ViewId } from '../context/ViewContext';
import { useInstance } from '../context/InstanceContext';
import { useItinerary } from '../context/ItineraryContext';

interface NavItem {
  id: ViewId;
  label: string;
  icon: React.ReactNode;
  /** Numeric count to surface in the tab badge. 0 / undefined hides it. */
  badgeCount?: number;
  /** Tone of the badge — `count` is theme primary; `alert` is theme alert. */
  badgeTone?: 'count' | 'alert';
}

const BottomNav: React.FC = () => {
  const { t } = useTranslation();
  const { view, setView } = useView();
  const { config } = useInstance();
  const { items: itinItems, counts, pendingCount } = useItinerary();

  // IA model: two canonical tabs (Chat, Trip). The legacy Bookings tab is
  // now folded into Trip's "Confirmed" filter — surfacing it as a
  // separate tab confused visitors who had already booked stuff but saw
  // an empty Bookings tab next to a populated Trip. The legacy Cart tab
  // (now "Pay") only appears when there's at least one pending-pay item;
  // its badge shows the count and switches to alert tone if the visitor
  // hasn't paid in a while. Partners can re-enable either with feature
  // flags for back-compat.
  const items: NavItem[] = useMemo(() => {
    const flags =
      (config?.feature_flags as Record<string, unknown> | undefined) || {};
    const showChat = flags.chat !== false;
    const showItinerary = flags.itinerary !== false;
    const legacyBookings = flags.legacy_bookings_tab === true;
    // Auto-show Pay tab only when there's something to pay — keeps the
    // chrome simple for first-time visitors. Can be force-shown by
    // setting flag.always_show_pay = true.
    const alwaysShowPay = flags.always_show_pay === true;
    const showPay = pendingCount > 0 || alwaysShowPay;

    const totalTrip = itinItems.length;
    const list: NavItem[] = [];
    if (showChat) {
      list.push({ id: 'chat', label: t('nav.chat'), icon: <ChatIcon /> });
    }
    if (showItinerary) {
      list.push({
        id: 'itinerary',
        label: t('nav.trip'),
        icon: <ItineraryIcon />,
        // Show the actual count — visitor wants to see "you have N
        // things planned" at a glance, not just a presence indicator.
        // Themed primary keeps it inside the brand palette.
        badgeCount: totalTrip,
        badgeTone: 'count',
      });
    }
    if (legacyBookings) {
      list.push({
        id: 'booking',
        label: t('nav.bookings'),
        icon: <BookingIcon />,
      });
    }
    if (showPay) {
      list.push({
        id: 'checkout',
        label: t('nav.pay'),
        icon: <PayIcon />,
        // Alert tone for "needs payment" — count makes urgency literal
        // instead of just decorating the icon with a dot.
        badgeCount: pendingCount,
        badgeTone: 'alert',
      });
    }
    return list;
  }, [t, config, itinItems.length, pendingCount, counts]);

  // A lone tab is chrome noise — with nothing to switch between (e.g. chat
  // disabled and no pending payments), drop the nav entirely.
  if (items.length < 2) return null;

  return (
    <nav className="wg-nav">
      {items.map(item => (
        <button
          key={item.id}
          type="button"
          className={`wg-nav__item${view === item.id ? ' is-active' : ''}`}
          onClick={() => setView(item.id)}
        >
          <span className="wg-nav__icon">
            {item.icon}
            {typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
              <span
                className={`wg-nav__badge wg-nav__badge--${item.badgeTone || 'count'}`}
                aria-label={`${item.badgeCount} ${item.label}`}
              >
                {item.badgeCount > 99 ? '99+' : item.badgeCount}
              </span>
            )}
          </span>
          <span className="wg-nav__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
};

export default BottomNav;

function ChatIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function ItineraryIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function BookingIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function CartIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6" />
    </svg>
  );
}

// "Pay" tab icon — credit-card glyph reads more bookably than a cart and
// matches the "what action remains" framing the tab carries.
function PayIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="10" y2="15" />
    </svg>
  );
}
